import Card from '../../components/ui/Card';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aufgabeApi, type AufgabeDue, type AufgabeStatusFilter, type AufgabeListItem } from '../../api/aufgabe';
import type { AufgabenEntityType } from '../../api/aufgaben';
import { downloadIcs, type IcsTask } from '../../utils/ics';

const ENTITY_LABELS: Record<AufgabenEntityType, string> = {
  verfahren: 'Verfahren',
  anfrage: 'Anfrage',
  inserat: 'Inserat',
  adresse: 'Adresse',
};

const ENTITY_BG: Record<AufgabenEntityType, string> = {
  verfahren: 'bg-purple-100 text-purple-700',
  anfrage: 'bg-blue-100 text-blue-700',
  inserat: 'bg-cyan-100 text-cyan-700',
  adresse: 'bg-amber-100 text-amber-700',
};

function entityPath(type: AufgabenEntityType, id: number): string {
  switch (type) {
    case 'verfahren': return `/verfahren/${id}`;
    case 'anfrage': return `/anfragen/${id}`;
    case 'inserat': return `/inserat/${id}`;
    case 'adresse': return `/adressen/${id}`;
  }
}

function isOverdue(d: string): boolean {
  return new Date(d) < new Date(new Date().toDateString());
}

function overdueLabel(d: string): string {
  const startOfToday = new Date(new Date().toDateString()).getTime();
  const dueDay = new Date(new Date(d).toDateString()).getTime();
  const days = Math.floor((startOfToday - dueDay) / 86400000);
  if (days <= 0) return '';
  return ` (überfällig seit ${days} ${days === 1 ? 'Tag' : 'Tagen'})`;
}

function taskToIcs(item: AufgabeListItem): IcsTask {
  return {
    uid: `aufgabe-${item.id}@fewo-monitor`,
    summary: item.title,
    date: (item.faelligkeit as string).slice(0, 10),
    description: [item.entity_title, item.description].filter(Boolean).join(' — ') || undefined,
    url: window.location.origin + entityPath(item.entity_type, item.entity_id),
  };
}

type View = 'meine' | 'zustaendig';

export default function AufgabenList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [view, setView] = useState<View>('meine');
  const [status, setStatus] = useState<AufgabeStatusFilter>('offen');
  const [typeFilter, setTypeFilter] = useState<AufgabenEntityType | 'all'>('all');
  const [due, setDue] = useState<AufgabeDue>(() => {
    const p = searchParams.get('due');
    return p === 'overdue' || p === 'today' || p === 'week' ? p : 'all';
  });

  const { data: mine, isLoading: mineLoading } = useQuery({
    queryKey: ['aufgaben', 'mine', status, typeFilter, due],
    queryFn: () => aufgabeApi.listMine(status, typeFilter === 'all' ? undefined : typeFilter, status === 'offen' ? due : 'all'),
    enabled: view === 'meine',
  });

  const { data: zust, isLoading: zustLoading } = useQuery({
    queryKey: ['aufgaben', 'zustaendig'],
    queryFn: () => aufgabeApi.zustaendig(),
    enabled: view === 'zustaendig',
  });

  const completeMutation = useMutation({
    mutationFn: (item: AufgabeListItem) => aufgabeApi.complete(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aufgaben'] }),
  });
  const reopenMutation = useMutation({
    mutationFn: (item: AufgabeListItem) => aufgabeApi.reopen(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aufgaben'] }),
  });

  const items = mine?.items ?? [];
  const withFaelligkeit = items.filter((i) => i.faelligkeit);

  const pill = (active: boolean) =>
    `px-3 py-1 rounded-full text-xs font-medium transition-colors ${
      active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Aufgaben</h1>
          {view === 'meine' && status === 'offen' && withFaelligkeit.length > 0 && (
            <button
              onClick={() => downloadIcs('aufgaben.ics', withFaelligkeit.map(taskToIcs))}
              className="btn btn-secondary bg-primary-50 text-primary-700 border-primary-200 enabled:hover:bg-primary-100 focus-visible:ring-primary-500 shrink-0 px-3 py-1.5 border"
              title="Alle Aufgaben mit Fälligkeit als Kalendereinträge exportieren"
            >
              Alle in Kalender exportieren
            </button>
          )}
        </div>

        {/* View tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex gap-6">
            <button
              onClick={() => setView('meine')}
              className={`py-2 text-sm font-medium border-b-2 ${view === 'meine' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Meine Aufgaben
            </button>
            <button
              onClick={() => setView('zustaendig')}
              className={`py-2 text-sm font-medium border-b-2 ${view === 'zustaendig' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Ich bin zuständig
            </button>
          </nav>
        </div>

        {view === 'meine' ? (
          <>
            {/* Status + filters */}
            <div className="flex flex-wrap gap-2 mb-2">
              <button onClick={() => setStatus('offen')} className={pill(status === 'offen')}>Offen</button>
              <button onClick={() => setStatus('erledigt')} className={pill(status === 'erledigt')}>Erledigt</button>
            </div>
            {status === 'offen' && (
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs text-gray-400 self-center mr-1">Fälligkeit:</span>
                <button onClick={() => setDue('all')} className={pill(due === 'all')}>Alle</button>
                <button onClick={() => setDue('overdue')} className={pill(due === 'overdue')}>Überfällig</button>
                <button onClick={() => setDue('today')} className={pill(due === 'today')}>Heute</button>
                <button onClick={() => setDue('week')} className={pill(due === 'week')}>Diese Woche</button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              {(['all', 'verfahren', 'anfrage', 'inserat', 'adresse'] as const).map((t) => (
                <button key={t} onClick={() => setTypeFilter(t)} className={pill(typeFilter === t)}>
                  {t === 'all' ? 'Alle Typen' : ENTITY_LABELS[t]}
                </button>
              ))}
            </div>

            {mineLoading ? (
              <Card className="p-8 text-center text-sm text-gray-500">Lädt...</Card>
            ) : items.length === 0 ? (
              <Card className="p-8 text-center text-sm text-gray-500">
                {status === 'offen' ? 'Keine offenen Aufgaben.' : 'Keine erledigten Aufgaben.'}
              </Card>
            ) : (
              <Card className="divide-y divide-gray-100">
                {items.map((item) => {
                  const overdue = item.status === 'offen' && item.faelligkeit && isOverdue(item.faelligkeit);
                  return (
                    <div key={item.id} className="flex items-start gap-3 px-4 py-4 hover:bg-gray-50">
                      <span className={`shrink-0 w-24 text-center px-2 py-0.5 rounded text-xs font-medium ${ENTITY_BG[item.entity_type]}`}>
                        {ENTITY_LABELS[item.entity_type]}
                      </span>
                      <button onClick={() => navigate(entityPath(item.entity_type, item.entity_id))} className="flex-1 min-w-0 text-left">
                        <p className={`text-sm font-medium ${item.status === 'erledigt' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {item.entity_title}
                          {item.entity_subtitle && <span> · {item.entity_subtitle}</span>}
                        </p>
                        {item.faelligkeit && (
                          <p className={`text-xs mt-0.5 font-medium ${overdue ? 'text-red-600' : 'text-gray-600'}`}>
                            Fällig: {new Date(item.faelligkeit).toLocaleDateString('de-DE')}{overdue ? overdueLabel(item.faelligkeit) : ''}
                          </p>
                        )}
                        {item.status === 'erledigt' && item.erledigt_at && (
                          <p className="text-xs text-gray-400 mt-0.5">Erledigt am {new Date(item.erledigt_at).toLocaleDateString('de-DE')}</p>
                        )}
                      </button>
                      <div className="shrink-0 flex flex-col items-end gap-1 text-xs">
                        {item.faelligkeit && (
                          <button onClick={() => downloadIcs(`aufgabe-${item.id}.ics`, [taskToIcs(item)])} className="btn btn-text">
                            In Kalender
                          </button>
                        )}
                        {item.status === 'offen' ? (
                          <button onClick={() => completeMutation.mutate(item)} className="btn btn-text text-success-700 enabled:hover:text-success-900 focus-visible:ring-success-500">Erledigt</button>
                        ) : (
                          <button onClick={() => reopenMutation.mutate(item)} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500">Wieder öffnen</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
          </>
        ) : (
          // Owner-Sicht
          zustLoading ? (
            <Card className="p-8 text-center text-sm text-gray-500">Lädt...</Card>
          ) : (zust ?? []).length === 0 ? (
            <Card className="p-8 text-center text-sm text-gray-500">
              Sie sind aktuell für keine Vorgänge zuständig.
            </Card>
          ) : (
            <Card className="divide-y divide-gray-100">
              {(zust ?? []).map((z) => (
                <button
                  key={`${z.entity_type}:${z.entity_id}`}
                  onClick={() => navigate(entityPath(z.entity_type, z.entity_id))}
                  className="w-full text-left flex items-start gap-3 px-4 py-4 hover:bg-gray-50"
                >
                  <span className={`shrink-0 w-24 text-center px-2 py-0.5 rounded text-xs font-medium ${ENTITY_BG[z.entity_type]}`}>
                    {ENTITY_LABELS[z.entity_type]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{z.title}</p>
                    {z.subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{z.subtitle}</p>}
                  </div>
                  {z.open_tasks > 0 && (
                    <span className="shrink-0 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                      {z.open_tasks} offen
                    </span>
                  )}
                </button>
              ))}
            </Card>
          )
        )}
      </main>
    </div>
  );
}
